import { Modal, Tag, Table, Button } from "antd";
import {
  X,
  FileText,
  Building2,
  Calendar,
  DollarSign,
  CreditCard,
  Hash,
  IndianRupee,
} from "lucide-react";
import { capitalizeFirstLetter } from "../../../utils";
import { dateTimeFormatter } from "../../../services/helper";

const sampleItems = [
  {
    key: "1",
    sNo: 1,
    description: "First Product",
    hsnCode: "HSN90782",
    gstRate: 3.0,
    quantity: 4.0,
    price: 200.0,
    totalAmount: 824.0,
  },
];

const getStatusStep = (status) => {
  switch (status) {
    case "paid":
      return 3;
    case "initiated":
      return 2;
    case "approved":
      return 1;
    case "pending":
      return 0;
    case "cancelled":
      return 0;
    default:
      return 0;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "paid":
      return "success";
    case "initiated":
      return "processing";
    case "pending":
      return "warning";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

export const InvoiceDetailsModal = ({ visible, onClose, invoice }) => {
  const itemColumns = [
    {
      title: "S. NO.",
      dataIndex: "sNo",
      key: "sNo",
      width: 80,
      align: "center",
      render: (data, record, index) => index + 1,
    },
    {
      title: "DESCRIPTION",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "HSN CODE",
      dataIndex: "hsn_code",
      key: "hsn_code",
      width: 120,
    },
    {
      title: "GST RATE",
      dataIndex: "gst_rate",
      key: "gst_rate",
      width: 100,
      render: (rate) => `${rate}%`,
    },
    {
      title: "QUANTITY",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (qty) => Number(qty),
    },
    {
      title: "PRICE",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 120,
      render: (price) => `₹${Number(price)}`,
    },
  ];

  if (!invoice) return null;
  console.log(invoice, "invoice");

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      className="invoice-details-modal"
      closeIcon={<X size={20} />}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText size={24} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Invoice Details
              </h2>
              <p className="text-gray-500">#{invoice.invoice_number}</p>
            </div>
          </div>
          <Tag
            color={getStatusColor(invoice.status)}
            className="px-4 py-2 text-sm font-medium"
          >
            {capitalizeFirstLetter(invoice.status)}
          </Tag>
        </div>

        {/* Modern Status Progress */}
        <div className="mb-10">
          <div className="relative">
            <div className="flex items-center justify-between">
              {/* Submitted */}
              <div className="flex flex-col items-center relative z-10">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 shadow-sm ${
                    getStatusStep(invoice.status) >= 0
                      ? "bg-blue-600 border-blue-600 text-white shadow-blue-200"
                      : "bg-white border-gray-300 text-gray-400 hover:border-gray-400"
                  }`}
                >
                  <FileText size={20} />
                </div>
                <span
                  className={`mt-3 text-sm font-semibold transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 0
                      ? "text-blue-600"
                      : "text-gray-400"
                  }`}
                >
                  Submitted
                </span>
                <div
                  className={`mt-1 text-xs transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 0
                      ? "text-blue-500"
                      : "text-gray-300"
                  }`}
                >
                  Step 1
                </div>
              </div>

              {/* Approved */}
              <div className="flex flex-col items-center relative z-10">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 shadow-sm ${
                    getStatusStep(invoice.status) >= 1
                      ? "bg-blue-600 border-blue-600 text-white shadow-blue-200"
                      : "bg-white border-gray-300 text-gray-400 hover:border-gray-400"
                  }`}
                >
                  <Building2 size={20} />
                </div>
                <span
                  className={`mt-3 text-sm font-semibold transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 1
                      ? "text-blue-600"
                      : "text-gray-400"
                  }`}
                >
                  Approved
                </span>
                <div
                  className={`mt-1 text-xs transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 1
                      ? "text-blue-500"
                      : "text-gray-300"
                  }`}
                >
                  Step 2
                </div>
              </div>

              {/* Initiated */}
              <div className="flex flex-col items-center relative z-10">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 shadow-sm ${
                    getStatusStep(invoice.status) >= 2
                      ? "bg-blue-600 border-blue-600 text-white shadow-blue-200"
                      : "bg-white border-gray-300 text-gray-400 hover:border-gray-400"
                  }`}
                >
                  <CreditCard size={20} />
                </div>
                <span
                  className={`mt-3 text-sm font-semibold transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 2
                      ? "text-blue-600"
                      : "text-gray-400"
                  }`}
                >
                  Initiated
                </span>
                <div
                  className={`mt-1 text-xs transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 2
                      ? "text-blue-500"
                      : "text-gray-300"
                  }`}
                >
                  Step 3
                </div>
              </div>

              {/* Paid */}
              <div className="flex flex-col items-center relative z-10">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 shadow-sm ${
                    getStatusStep(invoice.status) >= 3
                      ? "bg-green-600 border-green-600 text-white shadow-green-200"
                      : "bg-white border-gray-300 text-gray-400 hover:border-gray-400"
                  }`}
                >
                  <IndianRupee  size={20} />
                </div>
                <span
                  className={`mt-3 text-sm font-semibold transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 3
                      ? "text-green-600"
                      : "text-gray-400"
                  }`}
                >
                  Paid
                </span>
                <div
                  className={`mt-1 text-xs transition-colors duration-300 ${
                    getStatusStep(invoice.status) >= 3
                      ? "text-green-500"
                      : "text-gray-300"
                  }`}
                >
                  Step 4
                </div>
              </div>
            </div>

            {/* Progress Line */}
            <div className="absolute top-6 left-6 right-6 h-1 bg-gray-200 rounded-full -z-0">
              <div
                className="h-full bg-gradient-to-r from-blue-600 to-blue-500 rounded-full transition-all duration-700 ease-in-out shadow-sm"
                style={{
                  width: `${(getStatusStep(invoice.status) / 3) * 100}%`,
                }}
              />
              {/* Progress dots for visual enhancement */}
              <div className="absolute top-1/2 transform -translate-y-1/2 w-full flex justify-between px-1">
                {[0, 1, 2].map((step) => (
                  <div
                    key={step}
                    className={`w-2 h-2 rounded-full transition-all duration-500 ${
                      getStatusStep(invoice.status) > step
                        ? "bg-blue-400 shadow-sm"
                        : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText size={20} className="mr-2 text-blue-600" />
                Invoice Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Number:</span>
                  <span className="font-medium">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Bill Month:</span>
                  <span className="font-medium">{invoice.bill_month}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <Tag color={getStatusColor(invoice.status)}>
                    {capitalizeFirstLetter(invoice.status)}
                  </Tag>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">TDS Rate:</span>
                  <span className="font-medium">{invoice.tds_rate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Transaction ID:</span>
                  <span className="font-medium text-gray-500">
                    {invoice?.transaction_id || "N/A "}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Paid Amount:</span>
                  <span className="font-medium">₹{invoice?.paid_amount}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar size={20} className="mr-2 text-blue-600" />
                Payment Information
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Date:</span>
                  <span className="font-medium">
                    {dateTimeFormatter(invoice?.invoice_date)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-medium">₹{invoice?.total_amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Requestor Email:</span>
                  <span className="font-medium">
                    {invoice?.requester_email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payable Amount:</span>
                  <span className="font-bold text-lg text-blue-600">
                    ₹{invoice?.payable_amount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Date:</span>
                  <span className="font-medium text-gray-500">
                    {invoice?.payment_date
                      ? dateTimeFormatter(invoice?.payment_date)
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="mb-8">
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 size={20} className="mr-2 text-blue-600" />
              Company Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-2">
                  <div>
                    <span className="text-gray-600 text-sm">Company Name:</span>
                    <p className="font-medium">
                      {invoice?.company?.company_name}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600 text-sm">Address:</span>
                    <p className="font-medium">
                      {invoice?.company?.address1}-{invoice?.company?.address2}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <span className="text-gray-600 text-sm">
                    Company GST Number:
                  </span>
                  <p className="font-medium">
                    GSTN: {invoice?.company?.gst_number || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Hash size={20} className="mr-2 text-blue-600" />
            Items
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <Table
              columns={itemColumns}
              dataSource={invoice?.items || []}
              pagination={false}
              size="middle"
              className="invoice-items-table"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button size="large" onClick={onClose}>
            Close
          </Button>
          {/* <Button type="primary" size="large">
            Download PDF
          </Button> */}
        </div>
      </div>
    </Modal>
  );
};
